import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Banner, Empty, Segmented, errMsg } from '@/components/hr/shared'
import { useCurrency } from '@/contexts/CurrencyContext'
import { parseMoney } from '@/lib/trade'
import { toastHelpers } from '@/lib/toast-helpers'
import { cn } from '@/lib/utils'
import type { TradePriceRow } from '@/types/trade'
import { tradeRole } from './TradePage'

// Trade prices (F-TRD-N1). The default price every client pays, and — inside
// a client — that client's own price where it differs. Empty means "no price":
// a product without one can sit on a draft but never on a confirmed delivery.
// Changing a price never moves a delivery that is already confirmed.

export function PricesTab() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        The price every client pays unless they have their own. Set a client’s own prices from Clients → the client → Their prices.
      </p>
      <PriceTable />
    </div>
  )
}

export function PriceTable({ accountId }: { accountId?: string }) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  const { canSetup } = tradeRole()
  const [q, setQ] = useState('')
  const [only, setOnly] = useState<'priced' | 'all'>('all')
  const [edits, setEdits] = useState<Record<string, string>>({})
  const query = useQuery({ queryKey: ['trade', 'prices', accountId ?? ''], queryFn: () => apiClient.tradeListPrices(accountId) })
  const rows = (query.data?.data as TradePriceRow[] | undefined) ?? []
  useEffect(() => setEdits({}), [accountId])

  const own = (r: TradePriceRow) => (accountId ? r.client_price : r.default_price)
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase()
    return rows.filter(
      (r) =>
        (only === 'all' || own(r) != null || r.product_id in edits) &&
        (!s || r.name.toLowerCase().includes(s) || (r.category ?? '').toLowerCase().includes(s)),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, q, only, accountId])

  const changes = Object.entries(edits)
    .map(([product_id, text]) => ({ product_id, text, price: parseMoney(text), row: rows.find((r) => r.product_id === product_id) }))
    .filter((c) => c.row && c.price !== (own(c.row) ?? null))
  const invalid = changes.some((c) => c.price != null && Number.isNaN(c.price))

  const save = useMutation({
    mutationFn: () => apiClient.tradeSetPrices(changes.map((c) => ({ product_id: c.product_id, price: c.price })), accountId),
    onSuccess: () => {
      toastHelpers.success(`${changes.length} price${changes.length === 1 ? '' : 's'} saved`)
      setEdits({})
      qc.invalidateQueries({ queryKey: ['trade'] })
    },
    onError: (err) => toastHelpers.error('Couldn’t save the prices', errMsg(err)),
  })

  if (query.isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>
  if (rows.length === 0) return <Empty>Your menu has no products yet.</Empty>

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Segmented
            value={only}
            onChange={setOnly}
            options={[
              { id: 'all', label: 'All products' },
              { id: 'priced', label: accountId ? 'Their own prices' : 'Priced' },
            ]}
          />
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products" className="h-9 w-52 pl-8" />
          </div>
        </div>
        {canSetup && (
          <div className="flex gap-2">
            {changes.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setEdits({})}>
                Discard
              </Button>
            )}
            <Button size="sm" onClick={() => save.mutate()} disabled={!changes.length || invalid || save.isPending}>
              Save {changes.length ? `${changes.length} change${changes.length === 1 ? '' : 's'}` : 'prices'}
            </Button>
          </div>
        )}
      </div>
      {invalid && <Banner tone="danger">Prices must be numbers, zero or more.</Banner>}
      {shown.length === 0 ? (
        <Empty>{only === 'priced' ? 'No prices set yet.' : 'No products match.'}</Empty>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">Product</th>
                <th className="px-4 py-2 text-right font-medium">Menu price</th>
                {accountId && <th className="px-4 py-2 text-right font-medium">Default trade price</th>}
                <th className="px-4 py-2 text-right font-medium">{accountId ? 'Their price' : 'Trade price'}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {shown.map((r) => {
                const current = own(r)
                const text = edits[r.product_id] ?? (current != null ? String(current) : '')
                const changed = r.product_id in edits && parseMoney(edits[r.product_id]) !== (current ?? null)
                return (
                  <tr key={r.product_id}>
                    <td className="px-4 py-2">
                      <div className={cn('font-medium', !r.is_available && 'text-muted-foreground')}>{r.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {r.category ?? 'No category'}
                        {!r.is_available && ' · not on sale'}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right text-muted-foreground tabular-nums">{formatCurrency(r.retail_price)}</td>
                    {accountId && (
                      <td className="px-4 py-2 text-right text-muted-foreground tabular-nums">{r.default_price != null ? formatCurrency(r.default_price) : '—'}</td>
                    )}
                    <td className="px-4 py-2 text-right">
                      {canSetup ? (
                        <Input
                          inputMode="decimal"
                          value={text}
                          onChange={(e) => setEdits((m) => ({ ...m, [r.product_id]: e.target.value }))}
                          placeholder={accountId && r.default_price != null ? 'Default' : 'Not set'}
                          className={cn('ml-auto h-8 w-28 text-right tabular-nums', changed && 'border-primary')}
                          aria-label={`${accountId ? 'Their' : 'Trade'} price for ${r.name}`}
                        />
                      ) : (
                        <span className="tabular-nums">{current != null ? formatCurrency(current) : '—'}</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {accountId && <p className="text-xs text-muted-foreground">Leave “Their price” empty to charge the default trade price.</p>}
    </div>
  )
}
